import type { ReactElement } from 'react';
import { useState, useMemo, useEffect } from 'react';
import { Calendar, Download, X, User } from 'lucide-react';
import FilterInput from '../components/shared/FilterInput';
import { useToast } from '../hooks/useToast';

interface ReportEntry {
  id: string;
  nombre: string;
  fecha: string;
  ciudad: string;
  puntoVenta: string;
  evaluacion: number;
  representanteId: string;
  otcValue: number | null;
}

interface ReportsViewProps {
  onBack?: () => void;
  onNavigateToReprove?: () => void;
  onNavigate?: (section: 'reprove' | 'reports') => void;
}

const ReportsView = ({
  onBack,
  onNavigateToReprove,
  onNavigate,
}: ReportsViewProps): ReactElement => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [selectedRepresentante, setSelectedRepresentante] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [reports, setReports] = useState<ReportEntry[]>([]);
  const [representantes, setRepresentantes] = useState<
    Array<{ id: string; name: string }>
  >([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const { reproveDataService } = await import(
        '../services/reproveDataService'
      );
      const { wizardService } = await import('../services/wizardService');

      const repsData = await reproveDataService.getRepresentatives();

      setRepresentantes(repsData.map((r) => ({ id: r.id, name: r.name })));

      const response = await wizardService.reports.getReports();

      let reportsData: any[];
      if (Array.isArray(response)) {
        reportsData = response;
      } else if (response && 'data' in response) {
        reportsData = (response as any).data;
      } else {
        reportsData = [];
      }

      const uniqueRepIds = [
        ...new Set(reportsData.map((r) => r.representativeId)),
      ];

      const cityMap = new Map<string, string>();
      const storeMap = new Map<string, string>();

      for (const repId of uniqueRepIds) {
        try {
          const cities = await reproveDataService.getCitiesForRepresentative(
            repId
          );

          for (const city of cities) {
            cityMap.set(city.id, city.name);
          }

          for (const city of cities) {
            try {
              const storesResponse =
                await reproveDataService.getStoresForRepresentativeInCity(
                  repId,
                  city.id
                );

              const stores = Array.isArray(storesResponse)
                ? storesResponse
                : storesResponse.stores || [];

              for (const store of stores) {
                storeMap.set(store.id, store.name);
              }
            } catch (err) {
              console.warn(
                `No se pudieron cargar tiendas para ${repId} en ${city.id}`
              );
            }
          }
        } catch (err) {
          console.warn(`No se pudieron cargar ciudades para ${repId}`);
        }
      }

      const transformedReports: ReportEntry[] = reportsData.map((r) => {
        const repName =
          repsData.find((rep) => rep.id === r.representativeId)?.name ||
          'Desconocido';
        const cityName =
          cityMap.get(r.cityId) || `Ciudad (${r.cityId.substring(0, 8)})`;
        const storeName =
          storeMap.get(r.pointId) || `Tienda (${r.pointId.substring(0, 8)})`;

        return {
          id: r.id,
          nombre: repName,
          fecha: new Date(r.createdAt).toLocaleDateString('es-ES'),
          ciudad: cityName,
          puntoVenta: storeName,
          evaluacion: r.scorePct,
          representanteId: r.representativeId,
          otcValue: r.otcValue ?? null,
        };
      });

      setReports(transformedReports);
    } catch (err) {
      console.error('❌ Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (
        selectedRepresentante &&
        report.representanteId !== selectedRepresentante
      ) {
        return false;
      }

      if (dateFrom) {
        const reportDate = new Date(
          report.fecha.split('/').reverse().join('-')
        );
        const fromDate = new Date(dateFrom);
        if (reportDate < fromDate) return false;
      }

      if (dateTo) {
        const reportDate = new Date(
          report.fecha.split('/').reverse().join('-')
        );
        const toDate = new Date(dateTo);
        if (reportDate > toDate) return false;
      }

      return true;
    });
  }, [reports, selectedRepresentante, dateFrom, dateTo]);

  const getEvaluationColor = (score: number): string => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const clearFilters = (): void => {
    setSelectedRepresentante('');
    setDateFrom('');
    setDateTo('');
  };

  const exportToCSV = async (): Promise<void> => {
    try {
      addToast('info', 'Iniciando exportación...', 3000);

      // Cargar configuración directamente desde el endpoint correcto
      const { wizardService } = await import('../services/wizardService');
      const tabsFromDB = await wizardService.config.getAllTabs();

      if (!tabsFromDB || tabsFromDB.length === 0) {
        addToast('error', 'No hay configuración de preguntas disponible', 5000);
        return;
      }

      // Cargar detalles completos
      addToast(
        'info',
        `Cargando detalles de ${filteredReports.length} reportes...`,
        3000
      );

      const detailedReports = await Promise.all(
        filteredReports.map(async (report) => {
          try {
            const detail = await wizardService.reports.getReport(report.id);
            return { ...report, detail };
          } catch (err) {
            console.warn(`⚠️ Error cargando ${report.id}:`, err);
            return { ...report, detail: null };
          }
        })
      );
      // Ordenar por fecha descendente
      const sortedReports = detailedReports.sort((a, b) => {
        const dateA = new Date(
          a.fecha.split('/').reverse().join('-')
        ).getTime();
        const dateB = new Date(
          b.fecha.split('/').reverse().join('-')
        ).getTime();
        return dateB - dateA;
      });

      // Headers base
      const baseHeaders = [
        '#',
        'Nombre',
        'Fecha',
        'Ciudad',
        'Punto de Venta',
        'Evaluación (%)',
        'Cuota OTC (piezas)',
        'Comentarios',
      ];

      // Headers de preguntas usando tabs directamente de la BD
      const questionHeaders: string[] = [];
      const questionMapping: Array<{ tabIndex: number; questionId: string }> =
        [];

      // Filtrar solo tabs activos y ordenar por index
      const activeTabs = tabsFromDB
        .filter((tab) => tab.active)
        .sort((a, b) => a.index - b.index);

      activeTabs.forEach((tab) => {
        if (tab.questions && Array.isArray(tab.questions)) {
          const sortedQuestions = [...tab.questions].sort(
            (a, b) => a.order - b.order
          );

          sortedQuestions.forEach((q, idx) => {
            const questionText = q.text || 'Sin texto';
            const header = `${tab.title} - ${questionText}`;
            questionHeaders.push(header);

            questionMapping.push({
              tabIndex: tab.index,
              questionId: q.id,
            });
          });
        }
      });

      const allHeaders = [...baseHeaders, ...questionHeaders];

      // Construir filas
      addToast('info', 'Generando archivo CSV...', 2000);

      const rows = sortedReports.map((report, index) => {
        const sequentialId = String(index + 1);

        const baseData = [
          sequentialId,
          report.nombre,
          report.fecha,
          report.ciudad,
          report.puntoVenta,
          report.evaluacion.toFixed(1),
          report.otcValue != null ? String(report.otcValue) : 'N/A',
          report.detail?.comments ?? '',
        ];

        // Procesar respuestas
        const answerData: string[] = [];

        if (
          report.detail?.answers &&
          typeof report.detail.answers === 'object'
        ) {
          questionMapping.forEach((mapping, qIdx) => {
            const tabKey = String(mapping.tabIndex);
            const tabAnswers = report.detail.answers[tabKey];

            if (!tabAnswers || typeof tabAnswers !== 'object') {
              answerData.push('');
              return;
            }

            const answer = tabAnswers[mapping.questionId];

            let answerText = '';
            if (answer === true) {
              answerText = 'Sí';
            } else if (answer === false) {
              answerText = 'No';
            } else if (answer === 'na' || answer === 'N/A') {
              answerText = 'N/A';
            } else {
              answerText = '';
            }

            answerData.push(answerText);
          });

          const respondidas = answerData.filter((a) => a !== '').length;
        } else {
          questionMapping.forEach(() => answerData.push(''));
        }

        return [...baseData, ...answerData];
      });

      // Función para escapar valores
      const escapeCSV = (value: string | number): string => {
        const str = String(value);
        if (
          str.includes(',') ||
          str.includes('"') ||
          str.includes('\n') ||
          str.includes('\r')
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // BOM para UTF-8
      const BOM = '\uFEFF';

      const csvLines = [
        allHeaders.map(escapeCSV).join(','),
        ...rows.map((row) => row.map(escapeCSV).join(',')),
      ];

      const csvContent = BOM + csvLines.join('\r\n');

      // Nombre de archivo con timestamp
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');

      const filename = `reportes_reprove_${year}${month}${day}_${hours}${minutes}${seconds}.csv`;

      // Descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addToast(
        'success',
        `✅ Exportado: ${rows.length} reportes con ${questionHeaders.length} preguntas`,
        5000
      );
    } catch (error) {
      addToast(
        'error',
        `Error al exportar: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`,
        8000
      );
    }
  };

  const dateRangeText = useMemo(() => {
    if (dateFrom && dateTo) {
      return `${new Date(dateFrom).toLocaleDateString('es-ES')} - ${new Date(
        dateTo
      ).toLocaleDateString('es-ES')}`;
    }
    if (dateFrom) {
      return `Desde ${new Date(dateFrom).toLocaleDateString('es-ES')}`;
    }
    if (dateTo) {
      return `Hasta ${new Date(dateTo).toLocaleDateString('es-ES')}`;
    }
    return 'Todas las fechas';
  }, [dateFrom, dateTo]);

  const hasActiveFilters = useMemo(() => {
    return !!(selectedRepresentante || dateFrom || dateTo);
  }, [selectedRepresentante, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary-dark border-t-transparent rounded-full animate-spin" />
          <span className="text-sm sm:text-base text-gray-600">
            Cargando reportes...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Filtros */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <FilterInput
                label="Representante"
                icon={User}
                type="select"
                value={selectedRepresentante}
                onChange={(v) => {
                  setSelectedRepresentante(String(v));
                }}
                options={representantes}
                placeholder="Todos los representantes"
              />
            </div>

            <FilterInput
              label="Fecha desde"
              icon={Calendar}
              type="date"
              value={dateFrom}
              onChange={(v) => {
                setDateFrom(String(v));
              }}
            />

            <FilterInput
              label="Fecha hasta"
              icon={Calendar}
              type="date"
              value={dateTo}
              onChange={(v) => {
                setDateTo(String(v));
              }}
            />

            <div className="flex flex-col sm:flex-row gap-2 sm:col-span-2 lg:col-span-1">
              <button
                onClick={clearFilters}
                className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <X className="w-4" />
                <span>Borrar filtros</span>
              </button>
              <button
                onClick={exportToCSV}
                disabled={filteredReports.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 text-sm bg-primary-dark text-white rounded-md hover:bg-primary-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4" />
                <span>Exportar CSV</span>
              </button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 text-xs sm:text-sm text-gray-600 space-y-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="font-medium">Filtros activos:</span>
                <span>
                  {selectedRepresentante && selectedRepresentante !== 'all'
                    ? representantes.find((r) => r.id === selectedRepresentante)
                        ?.name
                    : 'Todos los representantes'}
                </span>
                <span className="hidden sm:inline">•</span>
                <span>{dateRangeText}</span>
              </div>
              <div className="font-medium text-primary-dark">
                {filteredReports.length} registros encontrados
              </div>
            </div>
          )}
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                    Nombre
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                    Fecha
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                    Ciudad
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm whitespace-nowrap">
                    Punto de Venta
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                    Evaluación
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReports.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 px-4 text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <div className="text-3xl sm:text-4xl mb-2">📊</div>
                        <div className="text-sm sm:text-base mb-2">
                          No se encontraron reportes
                        </div>
                        {hasActiveFilters && (
                          <button
                            onClick={clearFilters}
                            className="text-xs sm:text-sm text-primary-dark hover:text-primary-medium underline"
                          >
                            Limpiar filtros
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report) => (
                    <tr
                      key={report.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-left text-gray-900 text-sm">
                        {report.nombre}
                      </td>
                      <td className="py-3 px-4 text-left text-gray-600 text-sm whitespace-nowrap">
                        {report.fecha}
                      </td>
                      <td className="py-3 px-4 text-left text-gray-600 text-sm">
                        {report.ciudad}
                      </td>
                      <td className="py-3 px-4 text-left text-gray-600 text-sm">
                        {report.puntoVenta}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${getEvaluationColor(
                            report.evaluacion
                          )}`}
                        >
                          {report.evaluacion.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsView;
